/**
 * useDashboardFeed — Hub dashboard live-feed hook (HD-96).
 *
 * Polls `GET /v1/dashboard/feed?limit=20` every `POLL_MS` while the tab
 * is visible. Pauses when `document.hidden`, resumes (with an immediate
 * reload) on `visibilitychange`. Tracks consecutive failures so the UI
 * can swap to a retry chip after two.
 *
 * Auth: standard Cognito JWT — same posture as `useDashboardKpis`. Hub
 * API Gateway validates the JWT; the Hub Lambda's IAM role grants the
 * cross-product read on QR's DynamoDB. No QR-side JWT involved.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@d-m-organization/hub-shared-lib/auth';

// --- Public types ---------------------------------------------------------

export type Plan = 'free' | 'pro' | 'business';
export type FeedDeviceType = 'mobile' | 'desktop' | 'tablet' | 'other';

export interface FeedCountry { code: string; flag: string }

export interface FeedItem {
  id: string;
  timestampIso: string;
  deviceType: FeedDeviceType;
  country: FeedCountry | null;
  linkName: string;
  event: 'Scan';
}

export interface FeedBundle {
  window: 'recent';
  generatedAt: string;
  plan: Plan;
  upgradeRequired?: true;
  items: FeedItem[];
}

export type FeedState = 'idle' | 'loading' | 'ready' | 'error';
export interface FeedError { code: string; message: string }

export interface UseDashboardFeedResult {
  data: FeedBundle | null;
  state: FeedState;
  error: FeedError | null;
  consecutiveFailures: number;
  reload: () => void;
}

// --- Constants ------------------------------------------------------------

const POLL_MS = 30_000;
const DEFAULT_LIMIT = 20;

// --- Hook -----------------------------------------------------------------

interface ApiOk { ok: true; data: FeedBundle }
interface ApiErr { ok: false; error: string; message?: string }
type ApiBody = ApiOk | ApiErr;

async function fetchFeed(
  apiBase: string,
  token: string,
  limit: number,
  signal: AbortSignal,
): Promise<FeedBundle> {
  const url = `${apiBase}/v1/dashboard/feed?limit=${encodeURIComponent(String(limit))}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  let body: ApiBody;
  try {
    body = (await res.json()) as ApiBody;
  } catch {
    throw makeError(`http_${res.status}`, 'Unexpected response from feed API.');
  }
  if (!res.ok || !body.ok) {
    const err = body as ApiErr;
    throw makeError(err.error ?? `http_${res.status}`, err.message ?? 'Feed API error.');
  }
  return (body as ApiOk).data;
}

function makeError(code: string, message: string): FeedError & Error {
  const e = new Error(message) as FeedError & Error;
  e.code = code;
  e.message = message;
  return e;
}

export interface UseDashboardFeedOptions {
  limit?: number;
  pollMs?: number;
}

export function useDashboardFeed(opts: UseDashboardFeedOptions = {}): UseDashboardFeedResult {
  const { accessToken, isAuthReady, refreshSession } = useAuth();
  const limit = opts.limit ?? DEFAULT_LIMIT;
  const pollMs = opts.pollMs ?? POLL_MS;
  const apiBase = (import.meta as ImportMeta).env.VITE_API_BASE_URL as string | undefined;

  const [data, setData] = useState<FeedBundle | null>(null);
  const [state, setState] = useState<FeedState>('idle');
  const [error, setError] = useState<FeedError | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Fetcher — initial mount, `reloadKey` bumps, and reloads from the
  // interval/visibility handlers.
  useEffect(() => {
    if (!isAuthReady) return;
    if (!accessToken) {
      setState('idle');
      setData(null);
      setError(null);
      return;
    }
    if (!apiBase) {
      setState('error');
      setError({ code: 'config_missing', message: 'API base URL not configured.' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    setError(null);

    (async () => {
      try {
        const bundle = await fetchFeed(apiBase, accessToken, limit, controller.signal);
        if (controller.signal.aborted || !isMountedRef.current) return;
        setData(bundle);
        setConsecutiveFailures(0);
        setState('ready');
      } catch (err) {
        const code = (err as FeedError).code;
        // On 401, force-refresh once; effect re-runs when token updates.
        if (code === 'http_401' || code === 'Unauthorized') {
          try { await refreshSession(); return; } catch { /* fall through */ }
        }
        if (controller.signal.aborted || !isMountedRef.current) return;
        const e = err as FeedError;
        setError({
          code: e.code ?? 'unknown',
          message: e.message ?? 'Failed to load live feed.',
        });
        setConsecutiveFailures((n) => n + 1);
        setState('error');
      }
    })();

    return () => controller.abort();
  }, [apiBase, accessToken, isAuthReady, limit, reloadKey, refreshSession]);

  // Polling + visibility lifecycle (design §11.2.1).
  useEffect(() => {
    isMountedRef.current = true;
    if (typeof document === 'undefined') return;

    const startInterval = () => {
      if (intervalRef.current !== null) return;
      intervalRef.current = setInterval(() => reload(), pollMs);
    };
    const stopInterval = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        reload();
        stopInterval();
        startInterval();
      }
    };

    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      stopInterval();
    };
  }, [pollMs, reload]);

  return { data, state, error, consecutiveFailures, reload };
}
